import { useRef, useState } from 'react';
import { z } from 'zod';
import {
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Text,
  Title3,
  makeStyles,
} from '@fluentui/react-components';
import {
  acceptAttribute,
  checkDeclaredUpload,
  describeAccepted,
  getStepType,
  fileCategoryLabels,
  type ConfigOf,
} from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import type { StepPortalProps, StepReviewProps, StepUiModule } from '../contract.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  meta: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  picker: { display: 'none' },
  choose: { minHeight: '2.75rem', alignSelf: 'flex-start' },
  chosen: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  // 16px minimum, or iOS Safari zooms the page on focus and the rest goes off-screen.
  field: { fontSize: tokens.fontSizeBase300 },
});

const kernel = getStepType('date-credential');
type Config = ConfigOf<'date-credential'>;

/**
 * The draft is the submission shape with everything optional. Dates stay as the calendar
 * dates the person reads off the card — the kernel converts to instants where the engine
 * needs one, and storing what they actually typed is what makes the record answerable later.
 */
const draftSchema = z
  .object({
    identifier: z.string().nullable(),
    issuedOn: z.string().nullable(),
    expiresOn: z.string().nullable(),
    uploadId: z.string(),
    fileName: z.string(),
  })
  .partial();

function DateCredentialPortal({ config, draft, onDraftChange, disabled, upload }: StepPortalProps) {
  const styles = useStyles();
  const picker = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const parsed = kernel.configSchema.safeParse(config);
  if (!parsed.success) return <Text>This step is misconfigured. HR has been notified.</Text>;

  const settings: Config = parsed.data;
  const current = draftSchema.safeParse(draft ?? {});
  const value = current.success ? current.data : {};

  function set(next: Partial<z.infer<typeof draftSchema>>) {
    onDraftChange({ ...value, ...next });
  }

  async function choose(file: File | undefined) {
    if (file === undefined) return;
    setProblem(null);

    const verdict = checkDeclaredUpload(
      { contentType: file.type, byteLength: file.size },
      { acceptedTypes: settings.acceptedTypes, maxSizeMb: settings.maxSizeMb },
    );
    if (!verdict.ok) {
      setProblem(verdict.message);
      return;
    }

    setProgress(0);
    const result = await upload(file, setProgress);
    setProgress(null);
    if (!result.ok) {
      setProblem(result.message);
      return;
    }
    set({ uploadId: result.uploadId, fileName: result.fileName });
  }

  const busy = disabled || progress !== null;

  return (
    <div className={styles.block}>
      <Title3>{settings.heading}</Title3>
      {settings.instructions === '' ? null : <Text>{settings.instructions}</Text>}
      <div className={styles.meta}>
        <Caption1>Accepted: {describeAccepted(settings.acceptedTypes)}</Caption1>
        <Caption1>Up to {settings.maxSizeMb} MB</Caption1>
      </div>

      {problem === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>{problem}</MessageBarBody>
        </MessageBar>
      )}

      <input
        ref={picker}
        type="file"
        className={styles.picker}
        accept={acceptAttribute(settings.acceptedTypes)}
        onChange={(event) => {
          void choose(event.target.files?.[0]);
          event.target.value = '';
        }}
        disabled={busy}
      />
      <Button className={styles.choose} disabled={busy} onClick={() => picker.current?.click()}>
        {value.uploadId === undefined ? 'Choose a photo or file' : 'Choose a different file'}
      </Button>

      {progress === null ? null : <ProgressBar value={progress} max={1} thickness="large" />}

      {value.fileName === undefined ? null : (
        <div className={styles.chosen}>
          <Text weight="semibold">{value.fileName}</Text>
          <Caption1>Uploaded. It is not sent to us until you submit this step.</Caption1>
        </div>
      )}

      {settings.requiresIdentifier ? (
        <Field label={settings.identifierLabel} hint="Exactly as it appears on the card." required>
          <Input
            className={styles.field}
            value={value.identifier ?? ''}
            disabled={busy}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            onChange={(_, d) => set({ identifier: d.value === '' ? null : d.value })}
          />
        </Field>
      ) : null}

      {settings.collectsIssueDate ? (
        <Field label="Issue date">
          <Input
            className={styles.field}
            type="date"
            value={value.issuedOn ?? ''}
            disabled={busy}
            onChange={(_, d) => set({ issuedOn: d.value === '' ? null : d.value })}
          />
        </Field>
      ) : null}

      {/* The label has to match what the server will accept. `requiresExpiry` defaults to
          true, and submitting blank against a credential-date requirement is refused —
          so a field that says "leave blank" would invite a rejection, which is a worse
          failure than the one it replaced: the person does as they are told and is
          refused for it. */}
      <Field
        label="Expiry date"
        required={settings.requiresExpiry}
        hint={
          settings.requiresExpiry
            ? 'Exactly as printed on the document or card.'
            : 'Leave blank only if this document does not expire.'
        }
      >
        <Input
          className={styles.field}
          type="date"
          value={value.expiresOn ?? ''}
          disabled={busy}
          onChange={(_, d) => set({ expiresOn: d.value === '' ? null : d.value })}
        />
      </Field>
    </div>
  );
}

function DateCredentialReview({ config, submission }: StepReviewProps) {
  const styles = useStyles();
  const settings = kernel.configSchema.safeParse(config);
  const answer = kernel.submissionSchema.safeParse(submission);

  return (
    <div className={styles.block}>
      <Text weight="semibold">{settings.success ? settings.data.heading : 'Credential'}</Text>
      {answer.success ? (
        <>
          <Text>{answer.data.fileName}</Text>
          {/* Number and dates together: a reviewer checks the card against them side by side. */}
          {answer.data.identifier === null ? null : (
            <Text>
              {settings.success ? settings.data.identifierLabel : 'Number'}: {answer.data.identifier}
            </Text>
          )}
          <Caption1>
            {answer.data.issuedOn === null ? 'No issue date' : `Issued ${answer.data.issuedOn}`}
            {' · '}
            {answer.data.expiresOn === null ? 'No expiry' : `Expires ${answer.data.expiresOn}`}
          </Caption1>
        </>
      ) : (
        <Text>Nothing submitted yet.</Text>
      )}
      {settings.success ? <Caption1>Files to {fileCategoryLabels[settings.data.target]}</Caption1> : null}
    </div>
  );
}

export const dateCredentialUi: StepUiModule = { portal: DateCredentialPortal, review: DateCredentialReview };
